import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useFeature } from "@/contexts/FeaturesContext";

/*
 * Mesaje cu Cristian (chat asincron client↔terapeut) — golul identificat în analiza
 * concurenței (doar terapie.app îl avea). Continuitate între ședințe, direct în panou.
 */

export function MessagesPanel() {
  const enabled = useFeature("client_messages");
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/me/messages").then((r) => {
    setItems(r.data.items || []);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
  }).catch(() => {});
  useEffect(() => { if (enabled) load(); }, [enabled]);

  if (!enabled) return null;

  const send = async () => {
    if (text.trim().length < 1) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/messages", { text });
      setText("");
      toast.success(data.message, { duration: 5000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-premium p-6 max-w-2xl" data-testid="messages-panel">
      <h3 className="font-serif text-xl mb-1 inline-flex items-center gap-2">
        <MessageCircle size={18} style={{ color: "var(--ct-link)" }} /> Mesaje cu Cristian
      </h3>
      <p className="text-[11px] mb-4" style={{ color: "var(--ct-text-dim)" }}>
        Scrie-i oricând — între ședințe, când ai o întrebare sau doar vrei să spui cum îți e. Îți răspunde aici.
      </p>

      <div className="space-y-2.5 max-h-96 overflow-auto mb-4 pr-1" data-testid="messages-list">
        {items.length === 0 && (
          <p className="text-sm italic text-center py-6" style={{ color: "var(--ct-text-muted)" }}>
            Niciun mesaj încă. Primul pas e al tău — Cristian citește tot.
          </p>
        )}
        {items.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
              style={m.sender === "client"
                ? { background: "var(--ct-emerald)", color: "var(--ct-text-inverse)", borderBottomRightRadius: 6 }
                : { background: "var(--ct-cream-edge)", color: "var(--ct-text)", borderBottomLeftRadius: 6, border: "1px solid var(--ct-gold)" }}>
              {m.sender === "cristian" && <p className="text-[10px] font-medium mb-0.5" style={{ color: "var(--ct-gold)" }}>Cristian</p>}
              <p className="whitespace-pre-line">{m.text}</p>
              <p className="text-[9px] mt-1 opacity-60">{new Date(m.created_at).toLocaleString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2} maxLength={2000} className="premium-input flex-1 resize-none text-sm"
          placeholder="Scrie-i lui Cristian…" data-testid="message-input" />
        <button type="button" onClick={send} disabled={busy || text.trim().length < 1}
          className="btn-gold !px-4 self-end" data-testid="message-send"><Send size={16} /></button>
      </div>
      <p className="text-[10px] mt-2" style={{ color: "var(--ct-text-dim)" }}>
        Confidențial — doar tu și Cristian vedeți conversația.
      </p>
    </div>
  );
}
